// src/dashboard/components/Pages.tsx (or your existing path)

import React from "react";
import {
  BillingIcon,
  EventIcon,
  InstructionsIcon,
  ProfileIcon,
  SettingIcon,
} from "../../utils/CustomIcon";
import { useNavigate } from "react-router-dom";
import { pageNames } from "../../config/pageNames";
import ProgressAlert from "./ProgressAlert";
import useProfile from "profile/hooks/useProfile";
import { evalProfileProgress } from "dashboard/utils/evalProfileProgress";
import useOrgSettings from "settings/hooks/useIsoSettings";
import { evalOrgSettingsProgress } from "dashboard/utils/evalOrgSettingProgress";
import useEventCount from "event/hooks/useEventCount";
import { evalEventProgress } from "dashboard/utils/evalEventProgress";
import useUserSDG from "sdgselection/hooks/userSDGs";
import { evalISOProgress } from "dashboard/utils/evalISOProgress";
import { SubscriptionGate } from "common/components/subscription/SubscriptionGate";

import "../styles/pages.css";

const Pages: React.FC = () => {
  const navigate = useNavigate();
  const profile = useProfile();
  const orgSettings = useOrgSettings();
  const eventCount = useEventCount();
  const { userSDGList } = useUserSDG();

  const email = profile.userProfile?.email ?? null;

  return (
    <div className="dashboard-page mt-4 mb-3 mb-md-0">
      <div className="dashboard-page-header mb-4">
        <div>
          <p className="dashboard-kicker">Overview</p>
          <h1 className="dashboard-title">Welcome to your dashboard</h1>
          <p className="dashboard-subtitle">
            Complete your profile, manage your subscription, identify your issues, create a checklist of actions
            for your events
          </p>
        </div>
        {email && (
          <div className="dashboard-user-summary">
            <p className="dashboard-user-label">Signed in as</p>
            <p className="dashboard-user-email">{email}</p>
          </div>
        )}
      </div>

      <div className="row g-4 dashboard-grid">
        {/* My Profile */}
        <div className="col-md-6">
          <div
            className="dashboard-card"
            onClick={() => navigate(pageNames.PROFILE)}
          >
            <div className="dashboard-card-header">
              <div className="dashboard-card-icon">
                <ProfileIcon />
              </div>
              <div>
                <h3 className="dashboard-card-title">My Profile</h3>
                <p className="dashboard-card-meta">
                  Personal details
                </p>
              </div>
            </div>

            <p className="dashboard-card-body">
              Keep your profile up to date
            </p>

            <div className="dashboard-card-footer">
              <ProgressAlert
                data={profile?.userProfile}
                emptyText="Please complete your profile info"
                completionRateEval={evalProfileProgress}
              />
            </div>
          </div>
        </div>

        {/* ISO 20121 Evidence */}
        <div className="col-md-6">
          <div
            className="dashboard-card"
            onClick={() => navigate(pageNames.SETTINGS)}
          >
            <div className="dashboard-card-header">
              <div className="dashboard-card-icon">
                <SettingIcon />
              </div>
              <div>
                <a
                  href="https://www.converii.com/iso20121"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <h3 className="dashboard-card-title">ISO20121 Implementation</h3>
                </a>
                <p className="dashboard-card-meta">
                Implement the global standard for a sustainable event
                </p>
              </div>
            </div>

            <p className="dashboard-card-body">
              YOUR evidence of implementing ISO20121:2024
              can be used to acheive certification
            </p>

            <div className="dashboard-card-footer">
              <ProgressAlert
                data={orgSettings?.OrgSettings}
                emptyText="Please complete your ISO 20121 info"
                completionRateEval={evalOrgSettingsProgress}
              />
            </div>
          </div>
        </div>

        {/* My Events */}
        <div className="col-md-6">
          <div
            className="dashboard-card"
            onClick={() => navigate(pageNames.EVENT)}
          >
            <div className="dashboard-card-header">
              <div className="dashboard-card-icon">
                <EventIcon />
              </div>
              <div>
                <h3 className="dashboard-card-title">My Events</h3>
                <p className="dashboard-card-meta">
                  Create an event template and a list of actions
                </p>
              </div>
            </div>

            <p className="dashboard-card-body">
              Create YOUR event template and generate a
              list of credible and creative actions that you
              can choose to implement, amend or add your
              actions.
            </p>

            <div className="dashboard-card-footer">
              <ProgressAlert
                data={eventCount}
                emptyText="Please complete your event info"
                completedText={`You have ${eventCount.eventCount} events so far!`}
                completionRateEval={evalEventProgress}
              />
            </div>
          </div>
        </div>

        {/* Materiality Exercise */}
        <div className="col-md-6">
          <div
            className="dashboard-card"
            onClick={() => navigate(pageNames.SDG_SELECTED)}
          >
            <div className="dashboard-card-header">
              <div className="dashboard-card-icon">
                <InstructionsIcon />
              </div>
              <div>
                <h3 className="dashboard-card-title">Issue Identification</h3>
                <p className="dashboard-card-meta">
                  Focus on what matters most
                </p>
              </div>
            </div>

            <p className="dashboard-card-body">
              Use this materiality assessment to understand YOUR focus.
            </p>

            <div className="dashboard-card-footer">
              <ProgressAlert
                data={userSDGList}
                emptyText="Please complete your issue identification"
                completionRateEval={evalISOProgress}
              />
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="col-md-6">
          <div
            className="dashboard-card"
            onClick={() => navigate(pageNames.ACTIONS_INTRO)}
          >
            <div className="dashboard-card-header">
              <div className="dashboard-card-icon">
                <InstructionsIcon />
              </div>
              <div>
                <h3 className="dashboard-card-title">Instructions</h3>
                <p className="dashboard-card-meta">
                  How to get the most out of the app
                </p>
              </div>
            </div>

            <p className="dashboard-card-body">
              Understand how to use the platform optimally so you can get the
              best results and tailor it to your specific needs.
            </p>
          </div>
        </div>

        {/* Subscription / Billing */}
        <div className="col-md-6">
          <div
            className="dashboard-card"
            onClick={() => navigate(pageNames.BILLING)}
          >
            <div className="dashboard-card-header">
              <div className="dashboard-card-icon">
                <BillingIcon />
              </div>
              <div>
                <h3 className="dashboard-card-title">Your subscription</h3>
                <p className="dashboard-card-meta">
                  Upgrade your plan and billing
                </p>
              </div>
            </div>

            <p className="dashboard-card-body">
              Manage your subscription and unlock the full feature set of the
              platform, including premium tools and advanced insights.
            </p>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Pages;
